import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Plus, Pencil, Trash2, Printer, RefreshCw, Search, Eye } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { AuthContext } from "../../context/AuthContext";
import { QUOTATIONS_URL, QUOTATION_DETAIL_URL } from "../../constants/api";

const STATUS_BADGE = {
  draft:    "bg-gray-100 text-gray-700",
  sent:     "bg-blue-100 text-blue-700",
  viewed:   "bg-amber-100 text-amber-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  expired:  "bg-red-100 text-red-700",
};

const fmt = (v) => v && Number(v) > 0 ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

export default function QuotationsList() {
  const { fetch, remove } = useApiMethods();
  const nav = useNavigate();
  const { hasPermission, loginUser } = useContext(AuthContext);
  const isSuperAdmin = loginUser?.login_role === "super_admin";
  const canCreate    = isSuperAdmin || hasPermission("quotations", "create");
  const canEdit      = isSuperAdmin || hasPermission("quotations", "edit");
  const canDelete    = isSuperAdmin || hasPermission("quotations", "delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await fetch(QUOTATIONS_URL);
    if (res?.status === "success") setRows(res.data || []);
    else ToastMsg(res?.message || "Failed to load", "error");
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    if (!window.confirm("Delete quotation?")) return;
    const res = await remove(QUOTATION_DETAIL_URL(id));
    if (res?.status === "success") { ToastMsg("Deleted", "success"); load(); }
    else ToastMsg(res?.message || "Failed", "error");
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
        r.quote_number?.toLowerCase().includes(q) ||
        r.customer_name?.toLowerCase().includes(q) ||
        r.status?.toLowerCase().includes(q))
    : rows;

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Quotations</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Multi-plan proposals for prospects.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {canCreate && (
              <Button size="sm" onClick={() => nav("/quotations/new")}>
                <Plus className="h-3.5 w-3.5 mr-1" /> New Quotation
              </Button>
            )}
          </div>
        </div>

        <div className="relative w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search quote no., customer, status..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/40 text-left">
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Quote No.</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Date</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Valid Until</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Plans</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Value Range</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Status</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-300 w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.length === 0 && !loading ? (
                  <tr><td colSpan={8} className="text-center py-10 text-xs text-gray-400">No quotations yet</td></tr>
                ) : filtered.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20">
                    <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{r.quote_number}</td>
                    <td className="px-3 py-2.5 text-gray-700 dark:text-gray-200">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{r.quote_date ? dayjs(r.quote_date).format("DD MMM YYYY") : "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{r.valid_until ? dayjs(r.valid_until).format("DD MMM YYYY") : "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{r.plan_count}</td>
                    <td className="px-3 py-2.5 text-gray-700 dark:text-gray-200">
                      {r.min_grand_total === r.max_grand_total ? fmt(r.min_grand_total)
                        : `${fmt(r.min_grand_total)} – ${fmt(r.max_grand_total)}`}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[r.status] || "bg-gray-100 text-gray-600"}`}>
                          {r.status}
                        </span>
                        {r.approval_status === "pending" && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-100 text-yellow-800">
                            pending approval
                          </span>
                        )}
                        {r.approval_status === "approved" && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700">
                            approved
                          </span>
                        )}
                        {r.approval_status === "rejected" && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
                            rejected
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right space-x-1">
                      <button onClick={() => nav(`/quotations/${r.id}/print`)} title="Print" className="p-1 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"><Printer className="h-4 w-4" /></button>
                      {canEdit ? (
                        <button onClick={() => nav(`/quotations/${r.id}`)} title="Edit" className="p-1 text-primary hover:bg-primary/10 rounded"><Pencil className="h-4 w-4" /></button>
                      ) : (
                        <button onClick={() => nav(`/quotations/${r.id}`)} title="View" className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><Eye className="h-4 w-4" /></button>
                      )}
                      {canDelete && (
                        <button onClick={() => del(r.id)} title="Delete" className="p-1 text-red-500 hover:bg-red-500/10 rounded"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ContainerCard>
  );
}
